import clsx from "clsx";
import { NavLink, useLocation } from "react-router-dom";
import { FCC } from "types";

import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";

type TabWithDisableHintProps = {
  target: string;
  disabled: boolean;
  /** Single sentence explaining why the tab cannot be opened right now. */
  disabledHint?: string;
};

// Figma `dire-tab`: a pill that only fills in while its panel is shown. Text
// color is kept out of this base string and added per-branch below — two
// conflicting `text-*` utilities at equal specificity resolve by CSS source
// order, not JSX string order, so the disabled/enabled color can't just be
// appended on top of it.
export const direTabClassName =
  "group relative flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-body-base font-semibold leading-none tracking-[0.32px]";

export const TabWithDisableHint: FCC<TabWithDisableHintProps> = ({
  target,
  disabled,
  disabledHint,
  children,
}) => {
  const { pathname } = useLocation();
  // Targets are relative and carry the SIM in a query ("voice?simId=1"), so
  // the selected tab is read off the leading path segment — `NavLink` only
  // knows this for the tabs it renders, and a disabled tab is not one.
  const isSelected = pathname
    .split("/")
    .includes(target.split("?")[0].replace(/^\//, ""));

  if (disabled) {
    const tab = (
      <span
        className={clsx(
          direTabClassName,
          "cursor-not-allowed text-foreground-tertiary",
        )}
        role="tab"
        aria-disabled="true"
        aria-selected={isSelected}
        // Focusable without being followable, so keyboard users reach the tab
        // and hear why it is closed instead of skipping over it.
        tabIndex={disabledHint ? 0 : undefined}
      >
        <FocusRing />
        {/* Optical baseline centering for Gellix, as on the design system button. */}
        <span className="pb-1">{children}</span>
      </span>
    );

    return disabledHint ? (
      <HintTooltip content={disabledHint}>{tab}</HintTooltip>
    ) : (
      tab
    );
  }

  return (
    <NavLink
      className={({ isActive }) =>
        clsx(
          direTabClassName,
          "text-foreground-primary transition-colors hover:bg-surface-secondary active:bg-surface-secondary",
          isActive && "bg-surface-tertiary",
        )
      }
      to={target}
      replace
      role="tab"
      aria-selected={isSelected}
    >
      <FocusRing />
      <span className="pb-1">{children}</span>
    </NavLink>
  );
};
